import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Hook to manage dish save/bookmark functionality with optimistic updates
 */
export const useSaveDish = (dishId: string) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Query to check if dish is saved
  const { data: isSaved, isLoading } = useQuery({
    queryKey: ['saved-dish', dishId, user?.id],
    queryFn: async () => {
      if (!user) return false;
      
      const { data, error } = await supabase
        .from('saved_dishes')
        .select('id')
        .eq('user_id', user.id)
        .eq('dish_id', dishId)
        .maybeSingle();
      
      if (error) {
        console.error('[SAVE] Error checking saved status:', error);
        return false;
      }
      
      return !!data;
    },
    enabled: !!user && !!dishId,
  });

  // Mutation to toggle save status
  const toggleSaveMutation = useMutation({
    mutationFn: async ({ shouldSave }: { shouldSave: boolean }) => {
      if (!user) throw new Error('User not authenticated');
      
      console.log('[SAVE] 🔄 Toggling save status', { dishId, shouldSave, userId: user.id.slice(0, 8) });
      
      if (shouldSave) {
        const { error } = await supabase
          .from('saved_dishes')
          .insert({ user_id: user.id, dish_id: dishId });
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('saved_dishes')
          .delete()
          .eq('user_id', user.id)
          .eq('dish_id', dishId);
        
        if (error) throw error;
      }
      
      return shouldSave;
    },
    onMutate: async ({ shouldSave }) => {
      console.log('[SAVE] 🟢 Optimistic update started', { dishId, shouldSave });
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['saved-dish', dishId, user?.id] });
      await queryClient.cancelQueries({ queryKey: ['user-saved-dishes'] });
      
      // Snapshot the previous value
      const previousSaved = queryClient.getQueryData(['saved-dish', dishId, user?.id]);
      
      // Optimistically update to the new value
      queryClient.setQueryData(['saved-dish', dishId, user?.id], shouldSave);
      
      return { previousSaved };
    },
    onError: (err, variables, context) => {
      console.error('[SAVE] ❌ Save operation failed', { error: err, dishId });
      
      // Rollback to the previous value
      if (context?.previousSaved !== undefined) {
        queryClient.setQueryData(['saved-dish', dishId, user?.id], context.previousSaved);
      }
      
      toast.error('Failed to save dish. Please try again.');
    },
    onSuccess: (shouldSave) => {
      console.log('[SAVE] ✅ Save operation succeeded', { dishId, isSaved: shouldSave });
      toast.success(shouldSave ? 'Dish saved!' : 'Dish removed from saved');
    },
    onSettled: () => {
      // Only invalidate data that actually changes when saving a dish.
      // Feed queries (chartFeed, whatsNew, drools, feed) don't display per-item saved state,
      // so invalidating them on every save causes unnecessary full-feed refetches.
      queryClient.invalidateQueries({ queryKey: ['saved-dish', dishId, user?.id] });
      queryClient.invalidateQueries({ queryKey: ['user-saved-dishes'] });
      // Invalidate the specific dish so save_count stays in sync
      queryClient.invalidateQueries({ queryKey: ['dish', dishId] });
    },
  });

  const toggleSave = () => {
    if (!user) {
      toast.info('Please sign in to save dishes');
      return;
    }
    toggleSaveMutation.mutate({ shouldSave: !isSaved });
  };

  return {
    isSaved: isSaved ?? false,
    isLoading,
    toggleSave,
    isToggling: toggleSaveMutation.isPending,
  };
};

/**
 * Hook to fetch all saved dishes for the current user (for Profile page)
 */
export const useUserSavedDishes = (userId: string | undefined) => {
  return useQuery({
    queryKey: ['user-saved-dishes', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from('saved_dishes')
        .select(`
          id,
          created_at,
          dish_id,
          dishes (
            id,
            name,
            cuisine,
            vote_count,
            net_score,
            upvotes,
            downvotes,
            share_count,
            image_url,
            cover_image_url,
            service_type,
            slug,
            restaurants (
              name,
              city,
              state,
              google_place_id,
              latitude,
              longitude
            ),
            submission_tags (tag),
            dish_claims (dietary_claims (name, icon)),
            dish_platforms (platform_id, order_url),
            profiles!dishes_submitted_by_fkey (
              id,
              username,
              avatar_url,
              user_stats (dish_submissions)
            )
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('[SAVE] Error fetching saved dishes:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!userId,
  });
};
